'use client';

import React from 'react';
import { BookOpen, FileSpreadsheet, Scale, Languages, Phone, ArrowRight } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import AccountingPricingTable from '@/components/AccountingPricingTable';

export default function AccountingSection() {
  const { t } = useLanguage();

  const SERVICES = [
    {
      id: 'acc-1',
      icon: BookOpen,
      titleKey: 'acc.svc1.title',
      descKey: 'acc.svc1.desc',
      tagKeys: ['acc.svc1.tag1', 'acc.svc1.tag2'],
    },
    {
      id: 'acc-2',
      icon: FileSpreadsheet,
      titleKey: 'acc.svc2.title',
      descKey: 'acc.svc2.desc',
      tagKeys: ['acc.svc2.tag1', 'acc.svc2.tag2'],
    },
    {
      id: 'acc-3',
      icon: Scale,
      titleKey: 'acc.svc3.title',
      descKey: 'acc.svc3.desc',
      tagKeys: ['acc.svc3.tag1', 'acc.svc3.tag2'],
    },
    {
      id: 'acc-4',
      icon: Languages,
      titleKey: 'acc.svc4.title',
      descKey: 'acc.svc4.desc',
      tagKeys: ['acc.svc4.tag1', 'acc.svc4.tag2'],
    },
  ];

  const STATS = [
    { labelKey: 'acc.stat1.label', value: '12+', subKey: 'acc.stat1.sub' },
    { labelKey: 'acc.stat2.label', value: '400+', subKey: 'acc.stat2.sub' },
    { labelKey: 'acc.stat3.label', value: '5', subKey: 'acc.stat3.sub' },
    { labelKey: 'acc.stat4.label', value: '2,800+', subKey: 'acc.stat4.sub' },
  ];

  return (
    <section id="accounting" className="py-20 lg:py-28 bg-white">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10 lg:mb-12">
          <p className="text-xs font-semibold text-[#C9A96E] uppercase tracking-widest mb-3">{t('acc.tag')}</p>
          <h2 className="font-display text-4xl font-bold text-[#1B3A5C] tracking-tight mb-4 leading-tight">
            {t('acc.sectionTitle')?.split('\n')?.map((line, i) => (
              <React.Fragment key={i}>{line}{i === 0 && <br />}</React.Fragment>
            ))}
          </h2>
          <p className="text-slate-500 text-base leading-relaxed mb-8 max-w-3xl">
            {t('acc.sectionDesc')}
          </p>
        </div>

        <div id="accounting-pricing" className="mb-14 lg:mb-16 scroll-mt-24">
          <AccountingPricingTable t={t} defaultOpen={false} showToggle tightTop />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-start">
          {/* Left */}
          <div>
            <div className="space-y-3">
              {SERVICES?.map((svc) => {
                const Icon = svc?.icon;
                return (
                  <div key={svc?.id} className="flex gap-4 p-5 rounded-xl bg-slate-50 border border-slate-100 hover:border-emerald-200 hover:bg-emerald-50/30 transition-all duration-150 group">
                    <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0 mt-0.5 group-hover:bg-emerald-100 transition-colors">
                      <Icon size={18} className="text-emerald-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-slate-800 mb-1">{t(svc?.titleKey)}</p>
                      <p className="text-xs text-slate-500 leading-relaxed mb-2">{t(svc?.descKey)}</p>
                      <div className="flex gap-1.5">
                        {svc?.tagKeys?.map((tagKey) => (
                          <span key={tagKey} className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-semibold">{t(tagKey)}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right */}
          <div className="space-y-5">
            {/* Stats grid */}
            <div className="grid grid-cols-2 gap-4">
              {STATS?.map((stat) => (
                <div key={stat?.labelKey} className="bg-slate-50 border border-slate-200 rounded-xl p-5 text-center hover:border-[#1B3A5C]/20 transition-colors">
                  <p className="font-display font-bold text-3xl text-[#1B3A5C] tabular-nums mb-1">{stat?.value}</p>
                  <p className="text-xs font-semibold text-slate-600">{t(stat?.labelKey)}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">{t(stat?.subKey)}</p>
                </div>
              ))}
            </div>

            {/* CTA card */}
            <div className="relative bg-[#1B3A5C] rounded-2xl p-8 overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 rounded-full bg-white/4 -translate-y-1/2 translate-x-1/2" />
              <div className="relative z-10">
                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mb-5">
                  <Phone size={22} className="text-[#C9A96E]" />
                </div>
                <h3 className="font-display font-bold text-white text-xl mb-2">
                  {t('acc.cta.title')}
                </h3>
                <p className="text-slate-300 text-sm leading-relaxed mb-6">
                  {t('acc.cta.desc')}
                </p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {['🇷🇸 Srpski', '🇬🇧 English', '🇷🇺 Русский', '🇹🇷 Türkçe', '🇩🇪 Deutsch']?.map((lang) => (
                    <span key={lang} className="px-2.5 py-1 rounded-lg bg-white/8 border border-white/10 text-xs text-slate-300 font-medium">{lang}</span>
                  ))}
                </div>
                <a href="#contact" className="inline-flex items-center gap-2 px-6 py-3 bg-[#C9A96E] text-white text-sm font-semibold rounded-lg hover:bg-[#b8944f] active:scale-95 transition-all duration-150 w-full justify-center">
                  {t('acc.cta.btn')} <ArrowRight size={15} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}